import React, { useCallback, useEffect, useState } from 'react';
import { Activity, Download, Loader2, RefreshCw, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

/**
 * Task 6 — what has actually passed between Jarvis and Slack.
 *
 * TWO JOBS, AND THE SECOND ONE IS WHY IT IS WORTH BUILDING BEFORE ANYONE IS
 * USING IT.
 *
 *   The stated job: a client asks whether any of their task content has left
 *   Jarvis. This answers that, and the export is what gets sent to them.
 *
 *   The unstated one: on the day the workspace is first connected, this is the
 *   only place that says whether anything worked. Did the welcome arrive? Did
 *   the notification go out or was it withheld, and why? Did the button press
 *   come back? Without it, the first rollout is debugged by guesswork.
 *
 * DECISIONS NOT TO SEND ARE SHOWN, NOT HIDDEN.
 * A row reading "skipped — not connected" is the most common row in this table
 * today and it is the useful one: it proves the trigger fired and the
 * recipients resolved before any workspace existed. Filtering them out would
 * make an empty table look like a broken feature.
 */

interface Entry {
  id: number;
  created_at: string;
  direction: 'outbound' | 'inbound';
  pm_project_id: number | null;
  task_id: number | null;
  task_title: string | null;
  user_id: number;
  slack_user_id: string | null;
  trigger_type: string;
  inbound_action: string | null;
  status: string;
  reason: string | null;
  result: { field: string; before: string | null; after: string | null } | null;
}

/**
 * Plain English for every code the server can send. The server sends stable
 * codes, not sentences, so the wording lives here and only here.
 */
const REASONS: Record<string, string> = {
  not_installed: 'No Slack workspace connected',
  no_mapping: 'Not linked to a Slack account',
  not_permitted: 'Their notifications are switched off',
  muted: 'They muted this kind',
  duplicate: 'Already sent in the last 24 hours',
  self_acted: 'They made the change themselves',
  quiet_hours: 'Held for their quiet hours',
  applied: 'Applied in Jarvis',
  opened: 'Opened Jarvis',
  reply_invited: 'Asked them to reply in the thread',
  date_picker_offered: 'Offered a date picker',
  already_done: 'Task was already closed',
  bad_date: 'The date could not be read',
  comment_only: 'Added as a comment, nothing changed',
  field_change: 'Applied the change they asked for',
  status_with_comment: 'Moved the status and kept their words',
  unchanged: 'Already that value',
};

const TRIGGERS: Record<string, string> = {
  task_assigned: 'Task assigned',
  at_risk: 'Flagged At Risk',
  collaborator_added: 'Collaborator added',
  mention: '@Mention',
  comment_added: 'Comment on task',
  due_soon: 'Due soon',
  overdue: 'Overdue',
  weekly_digest: 'Monday digest',
  welcome: 'Welcome message',
};

const ACTIONS: Record<string, string> = {
  task_mark_done: 'Pressed Mark Done',
  task_change_date: 'Pressed Change Date',
  task_set_date: 'Picked a date',
  task_reply: 'Pressed Reply',
  task_open_in_jarvis: 'Opened in Jarvis',
  digest_open_in_jarvis: 'Opened Jarvis from the digest',
  thread_reply: 'Replied in the thread',
};

const STATUS_STYLES: Record<string, string> = {
  sent: 'text-green-700 bg-green-50',
  failed: 'text-red-700 bg-red-50',
  queued: 'text-blue-700 bg-blue-50',
  skipped: 'text-gray-600 bg-gray-100',
};

const PAGE = 25;

const when = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

const SlackActivityCard: React.FC = () => {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [direction, setDirection] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [exporting, setExporting] = useState('');

  const params = useCallback(() => {
    const p: Record<string, string | number> = { limit: PAGE, offset };
    if (direction) p.direction = direction;
    if (from) p.from = from;
    if (to) p.to = to;
    return p;
  }, [direction, from, to, offset]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/integrations/slack/notifications/log', { params: params() });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
      setFailed(false);
    } catch (err: any) {
      setFailed(true);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  /**
   * Downloads go through the same authenticated client as everything else, so
   * the file arrives as a blob rather than as a link the browser follows
   * without the session. A plain href would hit the endpoint unauthenticated
   * and download a 401 page named .csv.
   */
  const download = async (format: 'csv' | 'pdf') => {
    setExporting(format);
    try {
      const res = await api.get('/api/integrations/slack/notifications/log', {
        params: { ...params(), limit: undefined, offset: undefined, format },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `slack-activity${from || to ? `-${from || 'start'}_${to || 'now'}` : ''}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      // The server refuses a range too large to export in one file rather than
      // truncating it, and it says how much is in there. Passing that through
      // matters: "could not build the export" would send somebody looking for
      // a fault instead of narrowing the dates.
      let msg = 'Could not build the export.';
      if (err?.response?.status === 413) {
        try {
          const text = await (err.response.data as Blob).text();
          msg = JSON.parse(text).error || msg;
        } catch {
          msg = 'That date range is too large to export. Narrow it and try again.';
        }
      }
      toast.error(msg);
    } finally {
      setExporting('');
    }
  };

  const reset = (fn: () => void) => { setOffset(0); fn(); };

  return (
    <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4" data-testid="slack-activity-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-slate-50 rounded-lg">
            <Activity className="w-5 h-5 text-slate-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Slack activity</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Everything that has passed between Jarvis and Slack, including the messages
              Jarvis decided not to send. Export it to answer a client asking what left Jarvis.
            </p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          title="Refresh"
          data-testid="slack-activity-refresh"
          className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-40"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Direction</label>
          <select
            value={direction}
            onChange={(e) => reset(() => setDirection(e.target.value))}
            data-testid="slack-activity-direction"
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Both ways</option>
            <option value="outbound">Jarvis → Slack</option>
            <option value="inbound">Slack → Jarvis</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">From</label>
          <input
            type="date"
            value={from}
            onChange={(e) => reset(() => setFrom(e.target.value))}
            data-testid="slack-activity-from"
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">To</label>
          <input
            type="date"
            value={to}
            onChange={(e) => reset(() => setTo(e.target.value))}
            data-testid="slack-activity-to"
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="ml-auto flex gap-2">
          {(['csv', 'pdf'] as const).map((f) => (
            <button
              key={f}
              onClick={() => download(f)}
              disabled={Boolean(exporting) || total === 0}
              data-testid={`slack-activity-export-${f}`}
              title={total === 0 ? 'Nothing to export yet' : undefined}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
            >
              {exporting === f
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Download className="w-3.5 h-3.5" />}
              {f.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {failed ? (
        <div className="text-center py-6">
          <p className="text-sm text-gray-600">Could not load the activity log.</p>
          <button
            onClick={load}
            data-testid="slack-activity-retry"
            className="mt-2 text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            Try again
          </button>
        </div>
      ) : loading ? (
        <p className="text-xs text-gray-500 py-4">Loading the activity log…</p>
      ) : entries.length === 0 ? (
        <p className="text-xs text-gray-500 py-4" data-testid="slack-activity-empty">
          Nothing yet. Once a workspace is connected, every message Jarvis sends — and every
          one it decides not to send — appears here.
        </p>
      ) : (
        <>
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100" data-testid="slack-activity-rows">
            {entries.map((e) => (
              <div key={e.id} className="px-3 py-2.5 text-xs" data-testid={`slack-activity-row-${e.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2 min-w-0">
                    {e.direction === 'inbound'
                      ? <ArrowDownLeft className="w-3.5 h-3.5 text-indigo-500 mt-0.5 shrink-0" />
                      : <ArrowUpRight className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-gray-900 truncate">
                        {e.task_title || (e.task_id ? `Task ${e.task_id}` : TRIGGERS[e.trigger_type] || e.trigger_type)}
                      </p>
                      <p className="text-gray-500 mt-0.5">
                        {e.direction === 'inbound'
                          ? (ACTIONS[e.inbound_action || ''] || e.inbound_action || 'Came back from Slack')
                          : (TRIGGERS[e.trigger_type] || e.trigger_type)}
                        {' · '}
                        Jarvis user {e.user_id}
                        {e.slack_user_id ? ` · ${e.slack_user_id}` : ''}
                      </p>
                      {e.result && (
                        <p className="text-emerald-700 mt-0.5">
                          {e.result.field}: {e.result.before || '(none)'} → {e.result.after || '(none)'}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`inline-block px-1.5 py-0.5 rounded font-medium ${STATUS_STYLES[e.status] || 'text-gray-600 bg-gray-100'}`}>
                      {e.status}
                    </span>
                    <p className="text-gray-400 mt-1">{when(e.created_at)}</p>
                  </div>
                </div>
                {e.reason && (
                  <p className="text-gray-500 mt-1 ml-6">{REASONS[e.reason] || e.reason}</p>
                )}
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between text-xs text-gray-500">
            <span data-testid="slack-activity-count">
              {offset + 1}–{Math.min(offset + entries.length, total)} of {total}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setOffset(Math.max(offset - PAGE, 0))}
                disabled={offset === 0}
                data-testid="slack-activity-prev"
                className="px-2 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setOffset(offset + PAGE)}
                disabled={offset + entries.length >= total}
                data-testid="slack-activity-next"
                className="px-2 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default SlackActivityCard;
